import React, { useEffect, useState } from 'react';
import apiService from '../../services/ApiServices';
import { useNavigate } from 'react-router-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  FaEdit,
  FaTrash,
  FaLock,
  FaUnlock,
  FaPlus,
  FaCalendar,
  FaEye,
  FaCopy,
  FaGlobe,
  FaUserCircle,
  FaSearch,
  FaFilter,
  FaUsers,
  FaUser,
  FaRegClock,
  FaChevronLeft,
  FaChevronRight,
  FaStepBackward,
  FaStepForward,
  FaEllipsisV,
  FaCheckCircle,
  FaTimesCircle,
  FaQrcode,
  FaPrint
} from 'react-icons/fa';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Dropdown,
  InputGroup,
  Alert,
  Tooltip,
  OverlayTrigger,
  Pagination,
  Modal
} from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import LoadingOverlay from '../shared/LoadingOverlay';
import useLoading from '../shared/useLoading';

function EventsList() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info'); // 'success', 'danger', 'info'
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const navigate = useNavigate();
  const [userLink, setUserLink] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrValue, setQrValue] = useState('');
  const [qrTitle, setQrTitle] = useState('');
  const [qrEventDetails, setQrEventDetails] = useState(null);
  const { isLoading: isOverlayLoading, withLoading } = useLoading();

  const handleViewQr = (url, title, eventObj = null) => {
    setQrValue(url);
    setQrTitle(title);
    setQrEventDetails(eventObj);
    setShowQrModal(true);
  };

  const handlePrintQr = (url, title, eventObj = null) => {
    const qrSvg = renderToStaticMarkup(
      <QRCodeSVG value={url} size={200} level="H" />
    );
    const printWindow = window.open('', '_blank', 'width=600,height=500');
    if (!printWindow) {
      showAlertMessage('Pop-up blocked! Please allow pop-ups to print the QR Code.', 'danger');
      return;
    }
    const durationText = eventObj ? formatDuration(eventObj.duration) : '';
    const typeText = eventObj ? (getEventType(eventObj.locationtype)?.label || '') : '';
    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: #fff;
            }
            .print-container {
              text-align: center;
              padding: 2rem;
            }
            .print-title {
              font-size: 1.4rem;
              font-weight: 700;
              color: #1a1a2e;
              margin-bottom: 0.5rem;
            }
            .print-meta {
              font-size: 0.85rem;
              color: #6c757d;
              margin-bottom: 1.25rem;
            }
            .qr-wrapper {
              display: inline-block;
              padding: 1rem;
              border: 2px solid #e9ecef;
              border-radius: 12px;
              margin-bottom: 1rem;
            }
            .print-url {
              font-size: 0.7rem;
              color: #adb5bd;
              word-break: break-all;
              max-width: 320px;
              margin: 0 auto;
            }
            @media print {
              body { min-height: auto; }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="print-title">${title || 'QR Code'}</div>
            ${durationText || typeText ? '<div class="print-meta">' + (typeText ? typeText : '') + (typeText && durationText ? ' &bull; ' : '') + (durationText ? durationText : '') + '</div>' : ''}
            <div class="qr-wrapper">${qrSvg}</div>
            <div class="print-url">${url}</div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };


  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [totalPages, setTotalPages] = useState(1);

  const getEventlist = withLoading(async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/events/events/all');
      console.log('Fetched events:', response);
      setEvents(response.events || []);
      setUserLink(response.user_link || '');
    } catch (error) {
      console.error('Error fetching events:', error);
      showAlertMessage('Failed to load events');
    } finally {
      setLoading(false);
    }
  });

  const showAlertMessage = (message, type = 'info') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 4000);
  };

  const handleDelete = withLoading(async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        setActionLoading(id);
        console.log('Deleting event with id:', id);
        const deleteResponse = await apiService.delete(`/api/events/events/${id}`);
        console.log('Delete response:', deleteResponse);
        // Remove event from local state immediately for instant UI update
        setEvents(prev => prev.filter(e => e.id !== id));
        setSelectedEvents(prev => prev.filter(s => s !== id));
        showAlertMessage('Event deleted successfully!', 'success');
      } catch (error) {
        console.error('Error deleting event:', error);
        const errMsg = error?.response?.data?.message || error?.message || 'Failed to delete event';
        showAlertMessage(`Delete failed: ${errMsg}`, 'danger');
        // Refresh list to ensure UI is in sync with backend
        await getEventlist();
      } finally {
        setActionLoading(null);
      }
    }
  });

  const togglePrivate = withLoading(async (id, isprivate) => {
    try {
      setActionLoading(id);
      await apiService.put(`/api/events/events/${id}/toggle-private`, {
        isprivate: !isprivate
      });
      await getEventlist();
      showAlertMessage(`Event ${!isprivate ? 'made private' : 'made public'}`, 'success');
    } catch (error) {
      console.error('Error updating event:', error);
      showAlertMessage('Failed to update event privacy', 'danger');
    } finally {
      setActionLoading(null);
    }
  });

  const copyBookingLink = (link) => {
    const bookingUrl = `${window.location.origin}/booking/${link}`;
    navigator.clipboard.writeText(bookingUrl)
      .then(() => showAlertMessage('Booking link copied to clipboard!'))
      .catch(err => {
        console.error('Failed to copy link:', err);
        showAlertMessage('Failed to copy link');
      });
  };

  const gotoEvent = (id) => {
    navigate(`/events/${id}`);
  };

  const formatDuration = (duration) => {
    if (!duration) return 'Not set';
    try {
      const dur = JSON.parse(duration);
      return dur.normalvalue === 'custom' ? dur.customvalue.join(' ') : dur.normalvalue;
    } catch {
      return `${duration} min`;
    }
  };

  const getEventType = (locationtype) => {
    switch (locationtype?.toUpperCase()) {
      case 'GOOGLE_MEET_AND_CALENDAR':
        return { label: 'Google Meet • One-on-One', icon: <FaUser className="me-1" /> };
      case 'GROUP':
        return { label: 'Group Event', icon: <FaUsers className="me-1" /> };
      default:
        return { label: 'One-on-One', icon: <FaUser className="me-1" /> };
    }
  };

  const getAvailability = (event) => {
    return 'Weekdays, 9 am - 5 pm';
  };

  const getEventTypeBadgeVariant = (locationtype) => {
    switch (locationtype?.toUpperCase()) {
      case 'GOOGLE_MEET_AND_CALENDAR': return 'primary';
      case 'GROUP': return 'success';
      default: return 'secondary';
    }
  };

  // Filter events based on search and filter type
  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.locationtype?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filterType === 'all' ||
      (filterType === 'private' && event.isprivate) ||
      (filterType === 'public' && !event.isprivate);

    return matchesSearch && matchesFilter;
  });

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstItem, indexOfLastItem);

  // Update total pages when filtered events change
  useEffect(() => {
    const calculatedTotalPages = Math.ceil(filteredEvents.length / itemsPerPage);
    setTotalPages(calculatedTotalPages || 1);

    // Reset to first page if current page is beyond new total pages
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredEvents, itemsPerPage, currentPage]);

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  useEffect(() => {
    getEventlist();
  }, []);

  const handleSelectEvent = (id) => {
    setSelectedEvents(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedEvents.length === currentEvents.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(currentEvents.map(e => e.id));
    }
  };

  const getEventCountByType = () => {
    const total = events.length;
    const privateCount = events.filter(e => e.isprivate).length;
    const publicCount = total - privateCount;
    return { total, private: privateCount, public: publicCount };
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  // Generate pagination items
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    // Page numbers
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  const eventCounts = getEventCountByType();

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 bg-light">
        <Spinner animation="border" variant="primary" size="lg" className="mb-3" />
        <p className="text-muted">Loading your events...</p>
      </div>
    );
  }

  return (
    <Container fluid className="py-4 px-3 px-md-4 bg-light min-vh-100">
      <LoadingOverlay isLoading={isOverlayLoading} />
      {/* Alert Notification */}

      {showAlert && (
        <Alert
          variant={alertType}
          className="position-fixed top-0 start-50 translate-middle-x mt-3 shadow-sm"
          style={{ zIndex: 1050, minWidth: '300px' }}
          dismissible
          onClose={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      )}

      {/* Header Section */}
      <Row className="align-items-center mb-4">
        <Col xs={12} lg={6} className="mb-3 mb-lg-0">
          <div className="d-flex align-items-center">
            <div className="bg-primary rounded-circle p-3 me-3">
              <FaUserCircle className="text-white" size={28} />
            </div>
            <div>
              <h4 className="fw-bold text-dark mb-1">Event List</h4>
              <p className="text-muted mb-0">
                {eventCounts.total} {eventCounts.total === 1 ? 'event' : 'events'} total
              </p>
            </div>
          </div>
        </Col>

        <Col xs={12} lg={6} className="text-lg-end">
          <div className="d-flex flex-wrap justify-content-lg-end gap-2">
            <Button
              variant="outline-primary"
              className="d-flex align-items-center justify-content-center flex-grow-1 flex-md-grow-0"
              onClick={() => window.open(`/booking/${userLink}`, '_blank')}
              title="View your public booking page"
            >
              <FaGlobe className="me-2" /> Landing Page
            </Button>

            <Dropdown className="flex-grow-1 flex-md-grow-0">
              <Dropdown.Toggle
                variant="outline-secondary"
                className="d-flex align-items-center justify-content-center w-100"
                title="QR Code options for your profile landing page"
              >
                <FaQrcode className="me-2" /> QR Code
              </Dropdown.Toggle>
              <Dropdown.Menu className="shadow border-0">
                <Dropdown.Item onClick={() => handleViewQr(`${window.location.origin}/booking/${userLink}`, 'Profile Landing Page')}>
                  <FaEye className="me-2 text-info" /> View QR Code
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handlePrintQr(`${window.location.origin}/booking/${userLink}`, 'Profile Landing Page')}>
                  <FaPrint className="me-2 text-dark" /> Print QR Code
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button
              id="btn-new-event"
              variant="primary"
              className="d-flex align-items-center justify-content-center flex-grow-1 flex-md-grow-0"
              onClick={() => navigate('/events/new')}
              title="Create a new event type"
            > New Event
            </Button>
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}


      {/* Search and Filter Section */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="p-3">

          {/* ── Mobile layout (xs / sm) ── */}
          <div className="d-flex d-md-none align-items-center gap-2">
            {/* Search takes all remaining space */}
            <InputGroup className="flex-grow-1">
              <InputGroup.Text className="bg-light border-end-0">
                <FaSearch className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-start-0"
              />
            </InputGroup>

            {/* 3-dot filter menu */}
            <Dropdown
              show={showMobileFilter}
              onToggle={(isOpen) => setShowMobileFilter(isOpen)}
              align="end"
            >
              <Dropdown.Toggle
                variant={filterType !== 'all' ? 'primary' : 'outline-secondary'}
                size="sm"
                className="mobile-filter-toggle d-flex align-items-center"
                style={{ borderRadius: '8px', padding: '0.45rem 0.65rem' }}
              >
                <FaEllipsisV size={14} />
              </Dropdown.Toggle>

              <Dropdown.Menu className="shadow border-0 p-3" style={{ minWidth: '240px' }}>
                <div className="mb-1">
                  <small className="text-muted fw-semibold d-flex align-items-center mb-2">
                    <FaFilter className="me-1" /> Filter by visibility
                  </small>
                  <Form.Select
                    size="sm"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="mb-3"
                  >
                    <option value="all">All Events</option>
                    <option value="public">Public Only</option>
                    <option value="private">Private Only</option>
                  </Form.Select>

                  <small className="text-muted fw-semibold d-flex align-items-center mb-2">
                    <FaRegClock className="me-1" /> Items per page
                  </small>
                  <Form.Select
                    size="sm"
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  >
                    <option value={3}>3 per page</option>
                    <option value={6}>6 per page</option>
                    <option value={9}>9 per page</option>
                    <option value={12}>12 per page</option>
                  </Form.Select>
                </div>

                <div className="text-muted mt-2 pt-2 border-top">
                  <small>
                    Showing {Math.min(indexOfFirstItem + 1, filteredEvents.length)}–
                    {Math.min(indexOfLastItem, filteredEvents.length)} of {filteredEvents.length}
                  </small>
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* ── Desktop layout (md and above) ── */}
          <Row className="g-3 align-items-center d-none d-md-flex">
            <Col md={6} lg={5}>
              <InputGroup>
                <InputGroup.Text className="bg-light border-end-0">
                  <FaSearch className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search events by title or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-start-0"
                />
              </InputGroup>
            </Col>

            <Col md={6} lg={3}>
              <InputGroup>
                <InputGroup.Text className="bg-light border-end-0">
                  <FaFilter className="text-muted" />
                </InputGroup.Text>
                <Form.Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="all">All Events</option>
                  <option value="public">Public Only</option>
                  <option value="private">Private Only</option>
                </Form.Select>
              </InputGroup>
            </Col>

            {/* Items Per Page Selector */}
            <Col md={6} lg={2}>
              <InputGroup>
                <InputGroup.Text className="bg-light border-end-0">
                  <FaRegClock className="text-muted" />
                </InputGroup.Text>
                <Form.Select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                >
                  <option value={3}>3 per page</option>
                  <option value={6}>6 per page</option>
                  <option value={9}>9 per page</option>
                  <option value={12}>12 per page</option>
                </Form.Select>
              </InputGroup>
            </Col>

            {/* Results Count */}
            <Col md={6} lg={2}>
              <div className="text-muted text-center text-lg-end">
                <small>
                  Showing {Math.min(indexOfFirstItem + 1, filteredEvents.length)}–
                  {Math.min(indexOfLastItem, filteredEvents.length)} of {filteredEvents.length}
                </small>
              </div>
            </Col>
          </Row>

        </Card.Body>
      </Card>

      {/* Events List */}
      <Row className="g-3 mb-4">
        {currentEvents.length > 0 ? (
          currentEvents.map((event) => {
            const eventType = getEventType(event.locationtype);
            return (
              <Col xs={12} key={event.id}>
                <Card className="border-0 shadow-sm h-100 event-card">
                  <Card.Body className="p-4">
                    <Row className="align-items-center">
                      {/* Checkbox */}
                      <Col xs={1} className="pe-0">
                        <Form.Check
                          type="checkbox"
                          checked={selectedEvents.includes(event.id)}
                          onChange={() => handleSelectEvent(event.id)}
                          className="mt-0"
                        />
                      </Col>

                      {/* Event Details */}
                      <Col xs={12} md={7} lg={7}>
                        <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
                          <h6 className="fw-bold text-dark mb-0 me-1">{event.title}</h6>
                          <Badge
                            bg={getEventTypeBadgeVariant(event.locationtype)}
                            className="ms-2"
                          >
                            {eventType.icon}
                            {eventType.label}
                          </Badge>
                          {event.isprivate && (
                            <Badge bg="warning" text="dark" className="ms-2">
                              <FaLock size={10} /> Private
                            </Badge>
                          )}
                          <Badge bg={event.approval_needed ? 'info' : 'secondary'} className="ms-2 text-dark">
                            {event.approval_needed ? (
                              <>
                                <FaCheckCircle size={10} className="me-1" /> Approval required
                              </>
                            ) : (
                              <>
                                <FaTimesCircle size={10} className="me-1" /> Auto-confirm
                              </>
                            )}
                          </Badge>
                        </div>

                        <div className="d-flex flex-wrap align-items-center text-muted small mb-2">
                          <span className="d-flex align-items-center me-3">
                            <FaRegClock className="me-1" />
                            {formatDuration(event.duration)}
                          </span>
                          <span className="d-flex align-items-center">
                            <FaCalendar className="me-1" />
                            {getAvailability(event)}
                          </span>
                        </div>

                        <small className="text-muted">
                          Last updated: {new Date(event.updatedat || event.updatedat).toLocaleDateString()}
                        </small>
                      </Col>

                      {/* Actions */}
                      <Col xs={12} md={4} lg={4} className="mt-3 mt-md-0">
                        {/* ── Desktop action buttons (md+) ── */}
                        <div className="d-none d-md-flex flex-wrap gap-2 justify-content-end align-items-center">
                          {/* Copy Link Button */}
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Copy booking link</Tooltip>}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="d-flex align-items-center"
                              onClick={() => copyBookingLink(event.link)}
                              disabled={actionLoading === event.id}
                            >
                              {actionLoading === event.id ? (
                                <Spinner animation="border" size="sm" className="me-1" />
                              ) : (
                                <FaCopy className="me-1" />
                              )}
                              Copy Link
                            </Button>
                          </OverlayTrigger>

                          {/* Edit Button */}
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Edit this event</Tooltip>}
                          >
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="d-flex align-items-center"
                              onClick={() => gotoEvent(event.id)}
                              disabled={actionLoading === event.id}
                            >
                              {actionLoading === event.id ? (
                                <Spinner animation="border" size="sm" className="me-1" />
                              ) : (
                                <FaEdit className="me-1" />
                              )}
                              Edit
                            </Button>
                          </OverlayTrigger>

                          {/* Privacy Toggle Button */}
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{event.isprivate ? 'Make public' : 'Make private'}</Tooltip>}
                          >
                            <Button
                              variant={event.isprivate ? "outline-warning" : "outline-secondary"}
                              size="sm"
                              className="d-flex align-items-center"
                              onClick={() => togglePrivate(event.id, event.isprivate)}
                              disabled={actionLoading === event.id}
                            >
                              {actionLoading === event.id ? (
                                <Spinner animation="border" size="sm" className="me-1" />
                              ) : event.isprivate ? (
                                <FaUnlock className="me-1" />
                              ) : (
                                <FaLock className="me-1" />
                              )}
                              {event.isprivate ? 'Public' : 'Private'}
                            </Button>
                          </OverlayTrigger>

                          {/* View QR Code Button */}
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>View event QR Code</Tooltip>}
                          >
                            <Button
                              variant="outline-info"
                              size="sm"
                              className="d-flex align-items-center"
                              onClick={() => handleViewQr(`${window.location.origin}/booking/${event.link}`, event.title, event)}
                            >
                              <FaQrcode className="me-1" />
                              View QR
                            </Button>
                          </OverlayTrigger>

                          {/* Print QR Code Button */}
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Print event QR Code</Tooltip>}
                          >
                            <Button
                              variant="outline-dark"
                              size="sm"
                              className="d-flex align-items-center"
                              onClick={() => handlePrintQr(`${window.location.origin}/booking/${event.link}`, event.title, event)}
                            >
                              <FaPrint className="me-1" />
                              Print QR
                            </Button>
                          </OverlayTrigger>

                          {/* More Options Dropdown (desktop) */}
                          <Dropdown>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>More options</Tooltip>}
                            >
                              <Dropdown.Toggle
                                variant="outline-secondary"
                                size="sm"
                                className="d-flex align-items-center more-options-toggle"
                              >
                                <FaEllipsisV size={12} />
                              </Dropdown.Toggle>
                            </OverlayTrigger>

                            <Dropdown.Menu className="shadow-sm border-0">
                              <Dropdown.Item
                                onClick={() => handleDelete(event.id, event.title)}
                                className="d-flex align-items-center text-danger"
                                disabled={actionLoading === event.id}
                              >
                                <FaTrash className="me-2" /> Delete Event
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                onClick={() => copyBookingLink(event.link)}
                                className="d-flex align-items-center"
                              >
                                <FaCopy className="me-2" /> Copy Link
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => window.open(`/booking/${event.link}`, '_blank')}
                                className="d-flex align-items-center"
                              >
                                <FaEye className="me-2" /> Preview
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>

                        {/* ── Mobile action buttons (xs/sm only) ── */}
                        <div className="d-md-none evtlist-mobile-actions">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="d-flex align-items-center justify-content-center"
                            onClick={() => copyBookingLink(event.link)}
                            disabled={actionLoading === event.id}
                          >
                            <FaCopy className="me-1" /> Copy Link
                          </Button>

                          <Button
                            variant="outline-success"
                            size="sm"
                            className="d-flex align-items-center justify-content-center"
                            onClick={() => gotoEvent(event.id)}
                            disabled={actionLoading === event.id}
                          >
                            <FaEdit className="me-1" /> Edit
                          </Button>

                          <Dropdown className="evtlist-mobile-more-dropdown">
                            <Dropdown.Toggle
                              variant="outline-secondary"
                              size="sm"
                              className="d-flex align-items-center justify-content-center w-100 more-options-toggle"
                            >
                              <FaEllipsisV size={12} className="me-1" /> More
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="shadow-sm border-0">
                              <Dropdown.Item
                                onClick={() => togglePrivate(event.id, event.isprivate)}
                                className="d-flex align-items-center"
                                disabled={actionLoading === event.id}
                              >
                                {event.isprivate ? <FaUnlock className="me-2" /> : <FaLock className="me-2" />}
                                {event.isprivate ? 'Make Public' : 'Make Private'}
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                onClick={() => handleViewQr(`${window.location.origin}/booking/${event.link}`, event.title, event)}
                                className="d-flex align-items-center"
                              >
                                <FaQrcode className="me-2 text-info" /> View QR Code
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handlePrintQr(`${window.location.origin}/booking/${event.link}`, event.title, event)}
                                className="d-flex align-items-center"
                              >
                                <FaPrint className="me-2 text-secondary" /> Print QR Code
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                onClick={() => window.open(`/booking/${event.link}`, '_blank')}
                                className="d-flex align-items-center"
                              >
                                <FaEye className="me-2" /> Preview
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDelete(event.id, event.title)}
                                className="d-flex align-items-center text-danger"
                                disabled={actionLoading === event.id}
                              >
                                <FaTrash className="me-2" /> Delete Event
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            );
          })
        ) : (
          <Col xs={12}>
            <Card className="text-center py-5 border-0 shadow-sm">
              <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
                <FaCalendar className="text-muted" size={40} />
              </div>
              <h5 className="text-dark mb-2">No events found</h5>
              <p className="text-muted mb-4">
                {searchTerm || filterType !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Create your first event to get started'
                }
              </p>
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/events/new')}
                className="px-4"
              >
                <FaPlus className="me-2" /> Create Your First Event
              </Button>
            </Card>
          </Col>
        )}
      </Row>

      {/* Pagination */}
      {filteredEvents.length > 0 && totalPages > 1 && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-3">
            <Row className="align-items-center">
              <Col xs={12} md={6} className="mb-2 mb-md-0">
                <div className="text-muted">
                  Showing {Math.min(indexOfFirstItem + 1, filteredEvents.length)}-
                  {Math.min(indexOfLastItem, filteredEvents.length)} of {filteredEvents.length} events
                </div>
              </Col>

              <Col xs={12} md={6}>
                <div className="d-flex justify-content-center justify-content-md-end">
                  <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      <FaStepBackward />
                    </Pagination.First>

                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <FaChevronLeft />
                    </Pagination.Prev>

                    {renderPaginationItems()}

                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <FaChevronRight />
                    </Pagination.Next>

                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <FaStepForward />
                    </Pagination.Last>
                  </Pagination>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Bulk Actions - If multiple selected */}
      {selectedEvents.length > 0 && (
        <Card className="border-0 shadow-sm mt-4 bg-primary text-white">
          <Card.Body className="py-3">
            <Row className="align-items-center">
              <Col xs={12} md={6} className="mb-2 mb-md-0">
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    label={`${selectedEvents.length} event${selectedEvents.length > 1 ? 's' : ''} selected`}
                    checked={selectedEvents.length === currentEvents.length}
                    onChange={handleSelectAll}
                    className="text-white mb-0"
                  />
                </div>
              </Col>
              <Col xs={12} md={6} className="text-md-end">
                <div className="d-flex gap-2 justify-content-md-end">
                  <Button variant="light" size="sm">
                    <FaLock className="me-1" /> Make Private
                  </Button>
                  <Button variant="light" size="sm">
                    <FaUnlock className="me-1" /> Make Public
                  </Button>
                  <Button variant="outline-light" size="sm">
                    <FaTrash className="me-1" /> Delete Selected
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Custom Styles */}
      <style>{`
        .event-card {
    transition: all 0.3s ease;
    border-left: 4px solid transparent;
  }
        .event-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.1) !important;
    border-left-color: #3b82f6;
  }.more-options-toggle::after {
    display: none !important;
  }
  .dropdown-toggle.more-options-toggle {
    padding: 0.375rem 0.5rem;
    border-radius: 0.375rem;
  }
  .mobile-filter-toggle::after {
    display: none !important;
  }
        .bg-light {
          background-color: #f8f9fa !important;
        }
        .btn-outline-primary {
          border-color: #3b82f6;
          color: #3b82f6;
          font-weight: 500;
        }
        .btn-outline-success {
          border-color: #10b981;
          color: #10b981;
          font-weight: 500;
        }
        .btn-outline-warning {
          border-color: #f59e0b;
          color: #f59e0b;
          font-weight: 500;
        }
        .btn-outline-primary:hover {
          background-color: #3b82f6;
          color: white;
          transform: translateY(-1px);
        }
        .btn-outline-success:hover {
          background-color: #10b981;
          color: white;
          transform: translateY(-1px);
        }
        .btn-outline-warning:hover {
          background-color: #f59e0b;
          color: white;
          transform: translateY(-1px);
        }
        .card {
          border-radius: 12px;
        }
        .input-group-text, .form-control, .form-select {
          border-radius: 8px;
        }
        .pagination {
          margin-bottom: 0;
        }
        .page-link {
          border-radius: 6px;
          margin: 0 2px;
          border: none;
          color: #6c757d;
        }
        .page-item.active .page-link {
          background-color: #3b82f6;
          border-color: #3b82f6;
        }
        .page-link:hover {
          background-color: #e9ecef;
          color: #3b82f6;
        }
        /* Mobile action buttons grid */
        .evtlist-mobile-actions {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 8px;
        }
        .evtlist-mobile-actions .btn {
          font-size: 0.78rem;
          padding: 0.4rem 0.25rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .evtlist-mobile-more-dropdown {
          width: 100%;
        }
        @media (max-width: 768px) {
          .container-fluid {
            padding-left: 1rem;
            padding-right: 1rem;
          }
          .card-body {
            padding: 1rem !important;
          }
          .pagination {
            flex-wrap: wrap;
            justify-content: center;
          }
          .btn-group {
            width: 100%;
            margin-top: 0.5rem;
          }
        }
        @media (max-width: 576px) {
          .evtlist-mobile-actions {
            gap: 6px;
          }
          .evtlist-mobile-actions .btn {
            font-size: 0.72rem;
            padding: 0.35rem 0.2rem;
          }
        }
      `}</style>
      {/* QR Code Modal */}
      <Modal show={showQrModal} onHide={() => setShowQrModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center">
            <FaQrcode className="me-2 text-primary" /> QR Code
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column align-items-center justify-content-center py-4 bg-white">
          <h6 className="fw-bold text-dark mb-1 text-center">{qrTitle}</h6>
          {qrEventDetails && (
            <div className="d-flex flex-wrap justify-content-center gap-2 mb-3">
              <Badge bg="light" text="dark" className="border">
                <FaRegClock className="me-1" />
                {formatDuration(qrEventDetails.duration)}
              </Badge>
              <Badge bg="light" text="dark" className="border">
                <FaUser className="me-1" />
                {getEventType(qrEventDetails.locationtype)?.label}
              </Badge>
            </div>
          )}
          <div className="mb-3 p-4 border rounded-3 bg-light d-flex align-items-center justify-content-center" style={{ minHeight: '220px' }}>
            <QRCodeSVG value={qrValue} size={180} level="H" />
          </div>
          <small className="text-muted text-break text-center font-monospace" style={{ fontSize: '0.7rem', maxWidth: '320px' }}>{qrValue}</small>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 justify-content-center gap-2">
          <Button variant="outline-secondary" onClick={() => setShowQrModal(false)}>
            Close
          </Button>
          <Button variant="primary" className="d-flex align-items-center" onClick={() => handlePrintQr(qrValue, qrTitle, qrEventDetails)}>
            <FaPrint className="me-2" /> Print QR Code
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default EventsList;
